import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { PermissionMatrix } from "@/components/admin/PermissionMatrix";
import { KAPTEN_POWERS } from "@/lib/staff";

const SaveBtn = ({ busy, onClick, testid, children = "Simpan" }) => (
  <button type="button" onClick={onClick} disabled={busy} className="rk-btn-primary mt-4 text-sm" data-testid={testid}>
    {busy ? <Loader2 size={14} className="animate-spin" /> : children}
  </button>
);

const useSaver = (onSaved) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
};

export const DataTab = ({ target, groups, canEdit, onSaved }) => {
  const { user } = useAuth();
  const isMaster = user.role === "master";
  const [name, setName] = useState(target.name);
  const [username, setUsername] = useState(target.username);
  const [groupId, setGroupId] = useState(target.group_id || "");
  const [busy, run] = useSaver(onSaved);

  const save = () => {
    const body = { name, username };
    if (isMaster) Object.assign(body, groupId ? { group_id: groupId } : { clear_group: true });
    run(() => api.patch(`/staff/users/${target.id}`, body), "Data akun disimpan");
  };

  if (!canEdit) return <p className="text-sm text-zinc-500">Anda tidak punya wewenang mengubah data akun ini.</p>;
  return (
    <div className="space-y-3">
      <label className="block text-xs uppercase tracking-widest text-zinc-500">Nama</label>
      <input value={name} onChange={(e) => setName(e.target.value)} className="rk-input" data-testid="edit-name" />
      <label className="block text-xs uppercase tracking-widest text-zinc-500">Username</label>
      <input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} className="rk-input" data-testid="edit-username" />
      {isMaster ? (
        <>
          <label className="block text-xs uppercase tracking-widest text-zinc-500">Bagian</label>
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className="rk-input" data-testid="edit-group">
            <option value="">— Tanpa bagian —</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </>
      ) : null}
      <SaveBtn busy={busy} onClick={save} testid="save-data-btn" />
    </div>
  );
};

export const AccessTab = ({ target, groups, canEdit, onSaved }) => {
  const { user } = useAuth();
  const isMaster = user.role === "master";
  const group = groups.find((g) => g.id === target.group_id);
  const [mode, setMode] = useState(target.permission_mode === "group" && group ? "group" : "custom");
  const [perms, setPerms] = useState(target.permissions);
  const [busy, run] = useSaver(onSaved);

  const save = () => {
    const body = mode === "group" ? { permission_mode: "group" } : { permission_mode: "custom", permissions: perms };
    if (!isMaster) delete body.permission_mode;
    run(() => api.patch(`/staff/users/${target.id}`, body), "Hak akses disimpan — langsung berlaku");
  };

  if (!canEdit) return <p className="text-sm text-zinc-500">Anda tidak punya wewenang mengatur hak akses akun ini.</p>;
  return (
    <div>
      {isMaster && group ? (
        <div className="mb-4 flex items-center justify-between rounded-xl border border-white/10 bg-black/40 p-3">
          <div>
            <div className="text-sm font-medium">Ikuti hak akses Bagian "{group.name}"</div>
            <div className="text-[11px] text-zinc-500">OFF = atur hak akses khusus untuk orang ini saja</div>
          </div>
          <Switch checked={mode === "group"} onCheckedChange={(on) => setMode(on ? "group" : "custom")} data-testid="follow-group-switch" />
        </div>
      ) : null}
      {mode === "group" && group ? (
        <PermissionMatrix value={group.permissions} onChange={() => {}} disabled testidPrefix="group-view" />
      ) : (
        <PermissionMatrix value={perms} onChange={setPerms} cap={isMaster ? null : user.effective_permissions} testidPrefix="user-perm" />
      )}
      {!isMaster ? <p className="mt-2 text-[11px] text-zinc-500">Anda hanya bisa memberi akses yang juga Anda miliki.</p> : null}
      <SaveBtn busy={busy} onClick={save} testid="save-access-btn" />
    </div>
  );
};

export const PasswordTab = ({ target, canEdit, onSaved }) => {
  const [pw, setPw] = useState("");
  const [busy, run] = useSaver(onSaved);
  const save = () => {
    if (pw.length < 6) return toast.error("Password minimal 6 karakter");
    run(() => api.post(`/staff/users/${target.id}/password`, { new_password: pw }).then(() => setPw("")), "Password direset — sesi lama akun ini otomatis keluar");
  };
  if (!canEdit) return <p className="text-sm text-zinc-500">Anda tidak punya wewenang reset password akun ini.</p>;
  return (
    <div>
      <input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password baru (min. 6 karakter)" className="rk-input" data-testid="reset-password-input" />
      <SaveBtn busy={busy} onClick={save} testid="reset-password-btn">Reset Password</SaveBtn>
    </div>
  );
};

const CheckList = ({ items, selected, onToggle, empty, testidPrefix }) => (
  <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-xl border border-white/10 bg-black/40 p-3">
    {items.length === 0 ? <p className="text-xs text-zinc-500">{empty}</p> : null}
    {items.map((it) => (
      <label key={it.id} className="flex cursor-pointer items-center gap-2 text-sm">
        <Checkbox checked={selected.includes(it.id)} onCheckedChange={() => onToggle(it.id)} data-testid={`${testidPrefix}-${it.id}`} />
        {it.label}
      </label>
    ))}
  </div>
);

export const RoleTab = ({ target, groups, members, onSaved }) => {
  const scope = target.kapten;
  const [groupIds, setGroupIds] = useState(scope.managed_group_ids);
  const [memberIds, setMemberIds] = useState(scope.managed_member_ids);
  const [powers, setPowers] = useState(scope.powers);
  const [busy, run] = useSaver(onSaved);
  const toggle = (list, setList) => (id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const isKapten = target.role === "kapten";

  const setRole = (role) => run(() => api.post(`/staff/users/${target.id}/role`, { role }), role === "kapten" ? "Diangkat menjadi KAPTEN" : "Jabatan KAPTEN dicopot");
  const saveScope = () =>
    run(() => api.put(`/staff/users/${target.id}/kapten-scope`, { managed_group_ids: groupIds, managed_member_ids: memberIds, powers }), "Wewenang KAPTEN disimpan");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black/40 p-3">
        <div>
          <div className="text-sm font-medium">Jabatan saat ini: <b>{isKapten ? "KAPTEN" : "ANGGOTA"}</b></div>
          <div className="text-[11px] text-zinc-500">KAPTEN tidak otomatis mendapat semua akses.</div>
        </div>
        <button type="button" disabled={busy} onClick={() => setRole(isKapten ? "anggota" : "kapten")} className={isKapten ? "rk-btn-ghost text-xs text-rose-400" : "rk-btn-primary text-xs"} data-testid="toggle-kapten-btn">
          {isKapten ? "Copot KAPTEN" : "Angkat jadi KAPTEN"}
        </button>
      </div>
      {isKapten ? (
        <>
          <div>
            <div className="mb-1.5 text-xs uppercase tracking-widest text-zinc-500">Bagian yang diawasi</div>
            <CheckList items={groups.map((g) => ({ id: g.id, label: g.name }))} selected={groupIds} onToggle={toggle(groupIds, setGroupIds)} empty="Belum ada bagian." testidPrefix="scope-group" />
          </div>
          <div>
            <div className="mb-1.5 text-xs uppercase tracking-widest text-zinc-500">Anggota yang diawasi (tambahan)</div>
            <CheckList items={members.map((m) => ({ id: m.id, label: `${m.name} (@${m.username})` }))} selected={memberIds} onToggle={toggle(memberIds, setMemberIds)} empty="Belum ada anggota." testidPrefix="scope-member" />
          </div>
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Tindakan yang diizinkan</div>
            {KAPTEN_POWERS.map((p) => (
              <div key={p.key} className="flex items-center justify-between rounded-lg border border-white/5 px-3 py-2 text-sm">
                {p.label}
                <Switch checked={Boolean(powers[p.key])} onCheckedChange={(on) => setPowers({ ...powers, [p.key]: on })} data-testid={`power-${p.key}`} />
              </div>
            ))}
            <p className="text-[11px] text-zinc-500">Menu yang bisa dibuka KAPTEN sendiri diatur di tab "Hak Akses".</p>
          </div>
          <SaveBtn busy={busy} onClick={saveScope} testid="save-scope-btn">Simpan Wewenang</SaveBtn>
        </>
      ) : null}
    </div>
  );
};
